import React from 'react';
import { useQuery } from '@apollo/client';
import {
    Box,
    Divider,
    Group,
    Paper,
    Skeleton,
    Stack,
    Text,
    ThemeIcon,
} from '@mantine/core';
import { IconCalendarEvent } from '@tabler/icons-react';
import dayjs from 'dayjs';

import {
    GET_FUTURE_PRODUCT_PRICES,
    type GetFutureProductPricesQuery,
    type FutureProductPriceRow,
} from '../../../apollo/queries/futureProductPrices';
import formatCurrency from '../../../utility/formatCurrency';
import { useUser } from '../../../hooks/useUser';

const DEFAULT_MAX_ITEMS = 3;

function formatPriceTypeLabel(type: string): string {
    if (type === 'SellingPrice') {
        return 'SELLING PRICE';
    }
    if (type === 'CostPrice') {
        return 'COST PRICE';
    }
    return type.toUpperCase();
}

function FuturePriceEntryContent({ row }: { row: FutureProductPriceRow }) {
    return (
        <Stack gap="sm" className="min-w-0">
            <Text
                size="xs"
                fw={700}
                className="uppercase tracking-wider text-[#7D8FB3]"
            >
                {formatPriceTypeLabel(row.type)}
            </Text>
            <Text size="xl" fw={700} className="leading-none text-primary">
                {row.price == null
                    ? 'N/A'
                    : formatCurrency(row.price, row.currency ?? 'ZAR')}
            </Text>
            <Group gap="sm" align="flex-start" wrap="nowrap">
                <ThemeIcon
                    size={28}
                    radius="xl"
                    variant="light"
                    className="mt-0.5 shrink-0 bg-primary/15 text-primary"
                >
                    <IconCalendarEvent size={14} stroke={1.5} />
                </ThemeIcon>
                <Stack gap={2}>
                    <Text size="xs" className="text-[#4D5E80]">
                        Valid From:{' '}
                        {row.validFrom
                            ? dayjs(row.validFrom).format('DD MMM YYYY')
                            : '—'}
                    </Text>
                    <Text size="xs" className="text-[#4D5E80]">
                        Valid Until:{' '}
                        {row.validTo
                            ? dayjs(row.validTo).format('DD MMM YYYY')
                            : '—'}
                    </Text>
                </Stack>
            </Group>
        </Stack>
    );
}

type Props = {
    productId: string;
    skip: boolean;
    /** Drawer / nested surfaces: drop outer horizontal padding */
    embedded?: boolean;
    maxItems?: number;
};

export function FuturePricesQueryPanel({
    productId,
    skip,
    embedded = false,
    maxItems = DEFAULT_MAX_ITEMS,
}: Props) {
    const { isStoreManager } = useUser();
    const canSeeCostPrices = !isStoreManager;

    const { data, loading, error } = useQuery<GetFutureProductPricesQuery>(
        GET_FUTURE_PRODUCT_PRICES,
        {
            variables: { productId },
            skip: skip || !productId,
            fetchPolicy: 'cache-first',
        },
    );

    const outerClassName = embedded ? 'py-2' : 'px-6 pb-6 pt-4';

    if (loading) {
        return (
            <Stack gap="sm" className={outerClassName}>
                <Skeleton height={108} radius="md" />
            </Stack>
        );
    }

    if (error) {
        return (
            <Text
                size="sm"
                className={
                    embedded ? 'text-red-600 py-2' : 'text-red-600 px-6 py-4'
                }
            >
                {error.message ||
                    'Unable to load future prices. Please try again.'}
            </Text>
        );
    }

    const rows = (data?.getFutureProductPrices ?? [])
        .filter(row => canSeeCostPrices || row.type !== 'CostPrice')
        .slice(0, maxItems);

    if (rows.length === 0) {
        return (
            <Text
                size="sm"
                className={
                    embedded ? 'text-gray-600 py-2' : 'text-gray-600 px-6 py-4'
                }
            >
                No future-dated prices found for this product.
            </Text>
        );
    }

    if (rows.length === 1) {
        return (
            <Box className={outerClassName}>
                <Paper radius="md" className="bg-[#F4F5F7] px-4 py-4">
                    <FuturePriceEntryContent row={rows[0]} />
                </Paper>
            </Box>
        );
    }

    return (
        <Box className={outerClassName}>
            <Paper radius="md" className="overflow-hidden bg-[#F4F5F7]">
                <div className="flex w-full min-w-0 flex-nowrap items-stretch">
                    {rows.map((row: FutureProductPriceRow, i: number) => (
                        <React.Fragment key={row.id}>
                            {i > 0 && (
                                <Divider
                                    orientation="vertical"
                                    color="#e6e9ef"
                                    my="lg"
                                />
                            )}
                            <div className="flex min-w-0 flex-1 flex-col px-4 py-4">
                                <FuturePriceEntryContent row={row} />
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </Paper>
        </Box>
    );
}
